import { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';
import { DEMO_TENANT_ID } from './tenant';

export { DEMO_TENANT_ID };

export const MODULE_DEFS = [
    { key: 'graReturns', label: 'GRA / WMF pack + CBG Forms C–F + GRA ITAS', pack: true },
    { key: 'ussd', label: 'USSD / *920# verify', pack: true },
    { key: 'policeQr', label: 'Police QR + plate verify', pack: false, defaultOn: true },
    { key: 'clientPortal', label: 'Client portal', pack: true },
    { key: 'claimsFnol', label: 'Claims FNOL', pack: true },
    { key: 'ecowas', label: 'ECOWAS Brown Card add-on', pack: true }
];

export const OPTIONAL_RAIL_DEFS = [
    { id: 'wave', label: 'Wave' },
    { id: 'orange', label: 'Orange Money' },
    { id: 'qmoney', label: 'QMoney' },
    { id: 'pulsepay', label: 'Pulse Pay' }
];

export const ALL_RAILS = ['cash', 'wave', 'orange', 'qmoney', 'pulsepay'];

export function defaultModules() {
    return {
        graReturns: false,
        ussd: false,
        policeQr: true,
        clientPortal: false,
        claimsFnol: false,
        ecowas: false
    };
}

export function fullModules() {
    return {
        graReturns: true,
        ussd: true,
        policeQr: true,
        clientPortal: true,
        claimsFnol: true,
        ecowas: true
    };
}

export function defaultPayments() {
    return { enabledRails: ['cash'] };
}

export function fullPayments() {
    return { enabledRails: ALL_RAILS.slice() };
}

export function normalizeRail(value) {
    const m = String(value || '').toLowerCase().replace(/[\s_-]/g, '');
    if (m === 'orangemoney' || m === 'orange') return 'orange';
    if (m === 'pulse' || m === 'pulsepay') return 'pulsepay';
    if (m === 'wave') return 'wave';
    if (m === 'qmoney') return 'qmoney';
    if (m === 'cash') return 'cash';
    return m || 'cash';
}

export function normalizeModules(raw, fallback) {
    const base = fallback || defaultModules();
    if (!raw || typeof raw !== 'object') return { ...base };
    return {
        graReturns: !!raw.graReturns,
        ussd: !!raw.ussd,
        policeQr: raw.policeQr !== false,
        clientPortal: !!raw.clientPortal,
        claimsFnol: !!raw.claimsFnol,
        ecowas: !!raw.ecowas
    };
}

export function normalizePayments(raw, fallback) {
    const base = fallback || defaultPayments();
    if (!raw || !Array.isArray(raw.enabledRails)) return { enabledRails: base.enabledRails.slice() };
    const known = new Set(ALL_RAILS);
    const rails = [];
    raw.enabledRails.forEach((r) => {
        const id = normalizeRail(r);
        if (known.has(id) && !rails.includes(id)) rails.push(id);
    });
    if (!rails.includes('cash')) rails.unshift('cash');
    return { enabledRails: rails };
}

export function featuresFromTenant(data, tenantId) {
    const isDemo = tenantId === DEMO_TENANT_ID;
    const fallbackM = isDemo ? fullModules() : defaultModules();
    const fallbackP = isDemo ? fullPayments() : defaultPayments();
    const raw = data || {};
    return {
        modules: normalizeModules(raw.modules, fallbackM),
        payments: normalizePayments(raw.payments, fallbackP)
    };
}

export function persistableFeatures(modules, payments) {
    return {
        modules: normalizeModules(modules, defaultModules()),
        payments: normalizePayments(payments, defaultPayments())
    };
}

export function gambiaPackEnabled(modules) {
    return !!(modules && modules.graReturns && modules.ussd && modules.clientPortal && modules.claimsFnol && modules.ecowas);
}

export function withGambiaPack(modules, on) {
    return {
        ...(modules || defaultModules()),
        graReturns: !!on,
        ussd: !!on,
        clientPortal: !!on,
        claimsFnol: !!on,
        ecowas: !!on,
        policeQr: true
    };
}

export function toggleOptionalRail(payments, railId, on) {
    const id = normalizeRail(railId);
    if (id === 'cash') return normalizePayments(payments, defaultPayments());
    const current = normalizePayments(payments, defaultPayments()).enabledRails.filter((r) => r !== id);
    if (on) current.push(id);
    return { enabledRails: current };
}

export function railEnabled(features, rail) {
    const id = normalizeRail(rail);
    if (id === 'cash') return true;
    return !!(features && features.payments && features.payments.enabledRails && features.payments.enabledRails.includes(id));
}

export function moduleOn(features, key) {
    return !!(features && features.modules && features.modules[key]);
}

export function defaultPaymentMethod() {
    return 'cash';
}

export function coercePaymentMethod(method, features) {
    const id = normalizeRail(method);
    return railEnabled(features, id) ? id : 'cash';
}

export function hasWalletRails(features) {
    return !!(features && features.payments && features.payments.enabledRails
        && features.payments.enabledRails.some((r) => r !== 'cash'));
}

export function useTenantFeatures(tenantId) {
    const [features, setFeatures] = useState(() => featuresFromTenant({}, tenantId));
    useEffect(() => {
        if (!tenantId) return undefined;
        let live = true;
        getDoc(doc(db, 'tenants', tenantId)).then((snap) => {
            if (live && snap.exists()) setFeatures(featuresFromTenant(snap.data(), tenantId));
        }).catch(() => {});
        return () => { live = false; };
    }, [tenantId]);
    return features;
}

export function useDirectoryFeatures(tenantId) {
    const [features, setFeatures] = useState(() => featuresFromTenant({}, tenantId));
    useEffect(() => {
        if (!tenantId) return undefined;
        let live = true;
        getDoc(doc(db, 'directory', tenantId)).then((snap) => {
            if (live && snap.exists()) setFeatures(featuresFromTenant(snap.data(), tenantId));
        }).catch(() => {});
        return () => { live = false; };
    }, [tenantId]);
    return features;
}
